import { inject, Injectable } from '@angular/core';
import { combineLatest, filter, map, Observable, shareReplay, switchMap, withLatestFrom } from 'rxjs';
import { CostRule, WorkingHours } from 'src/app/api/v1/models';
import { ObjectKeyArray } from 'src/app/shared/interfaces/object-key-array.interface';
import { ConfigRepository } from 'src/app/shared/repositories/config.repository';
import { SessionRepository } from 'src/app/shared/repositories/session.repository';
import { getNowByTZ } from 'src/app/shared/utils/convert-timezone';
import { DateTime } from 'src/app/shared/utils/date-time';
import { FilterIntervalsPipe } from './filter-intervals.pipe';

export interface DeliveryTimeInterval {
  date: Date,
  rules: CostRule[],
}

@Injectable({
  providedIn: 'root'
})
export class StepTimeService {
  private configRepository = inject(ConfigRepository);
  private sessionRepository = inject(SessionRepository);

  intervals: DeliveryTimeInterval[];

  today = getNowByTZ(this.configRepository.restoTimeZone);
  yesterday = new Date(getNowByTZ(this.configRepository.restoTimeZone).setDate(this.today.getDate() - 1));
  checkFirstInterval: boolean = false;

  private workingHours$: Observable<ObjectKeyArray<WorkingHours> | null> = this.sessionRepository.isDelivery$.pipe(
    switchMap((isDelivery: boolean) => isDelivery
      ? this.configRepository.deliveryAvailableIntervals$
      : this.configRepository.pickupAvailableIntervals$
    ),
    filter(workingHours => !!workingHours),
  );

  private durationInMinutes$: Observable<number> = this.sessionRepository.isDelivery$.pipe(
    switchMap((isDelivery: boolean) => isDelivery
      ? this.configRepository.deliveryDurationInMinutes$
      : this.configRepository.pickupDurationInMinutes$
    ),
  );

  intervals$: Observable<DeliveryTimeInterval[]> = combineLatest([
    this.workingHours$,
    this.configRepository.orderIntervalInMinutes$,
  ]).pipe(
    map(([hours, step]) => this.setIntervals(hours, step)),
    shareReplay(1)
  );

  daysRange$ = this.intervals$.pipe(
    withLatestFrom(this.durationInMinutes$),
    map(([intervals, durationInMinutes]) => {
      if(!intervals) {
        return [];
      }

      const deliveryTime = new Date(this.today.getTime() + durationInMinutes * 60000);
      const isMidnightTransition = deliveryTime.getDate() - this.today.getDate() > 0;

      const calendarDates = [...new Set(intervals
        .filter(interval => interval.date >= deliveryTime)
        .map((interval: DeliveryTimeInterval) => interval.date.getDate())
      )];

      if(isMidnightTransition &&
        intervals.filter(i => i.date.getDate() === this.today.getDate() + 1 && i.date <= deliveryTime).length) {
        calendarDates.unshift(this.today.getDate());
      }

      let month = this.today.getMonth();

      return calendarDates.map((date: number, index: number) => {
        const dateItem = new Date();

        dateItem.setDate(date);

        if(index > 0 && date < calendarDates[index - 1] || index === 0 && date < this.today.getDate()) {
          month++;
        }
        dateItem.setMonth(month);

        const nowTime = this.today.getTime();

        const openTime = intervals.find(interval => interval.date.getDate() === date)?.date.getTime() ?? nowTime;
        dateItem.setTime(nowTime < openTime ? openTime : nowTime);

        return dateItem;
      });
    })
  );

  hasIntervals$ = this.intervals$.pipe(
    withLatestFrom(this.durationInMinutes$),
    map(([intervals, durationInMinutes]) => {
      const currentIntervals = new FilterIntervalsPipe().transform(
        intervals, this.today, durationInMinutes, this.configRepository.restoTimeZone
      );
      const openTime = intervals.find(interval => interval.date.getDate() === this.today.getDate())?.date ?? this.today;

      return this.today >= openTime && !!currentIntervals.length;
    }),
  );

  nearestDeliveryDate$: Observable<DeliveryTimeInterval | null> = this.intervals$.pipe(
    withLatestFrom(this.daysRange$),
    map(([intervals, daysRange]) => {
      if(!daysRange || !intervals.length) {
        return null;
      }
      return intervals
        .filter(interval => interval.date.getDate() === daysRange[0].getDate())
        .reduce((a, b) => a.date <= b.date ? a : b); // берем наименьший
    })
  );

  setIntervals(
    workingHours: ObjectKeyArray<WorkingHours> | null,
    step: number,
  ): DeliveryTimeInterval[] {

    if(!workingHours) {
      return [];
    }

    this.intervals = [];

    const twoWeeksDateRange = this.getDateRange(this.yesterday, 15);

    twoWeeksDateRange.forEach((date: DateTime) => {
      const intervalOfDate = this.getIntervals(date, workingHours, step);
      this.intervals.push(...intervalOfDate);
    });

    return this.intervals;
  }

  private getDateRange(now: Date, days: number): DateTime[] {
    const startDate = new DateTime(now).startOfDay();
    const range = DateTime.range(startDate.addDays(1), startDate.addDays(days), DateTime.DAY_IN_MS);

    return [new DateTime(now), ...range];
  }

  private getIntervals(
    date: DateTime,
    workingHours: ObjectKeyArray<WorkingHours>,
    step: number,
  ): DeliveryTimeInterval[] {
    if(!workingHours) {
      return [];
    }

    const intervalMinutes = step * DateTime.MINUTE_IN_MS;
    const dayIntervals: WorkingHours[] = workingHours[date.weekDay()];
    const intervals: DeliveryTimeInterval[] = [];

    if(!dayIntervals) {
      return [];
    }

    for(const interval of dayIntervals) {
      const start = new DateTime(date).startOfWorkingHours(interval);
      let close = new DateTime(date).endOfWorkingHours(interval);
      if(start.getTime() > close.getTime()) {
        close = close.addDays(1);
      }
      const range = DateTime.range(start, close, intervalMinutes);
      range.forEach((date: Date) => intervals.push({date, rules: interval.costRules}));
    }

    return intervals;
  }
}
